import { useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks';
import { IArea, IMember } from '../../../types';
import { ActionItem } from 'styles/animations.styled';
import { Item, StyledSpan, ToggleWrapper, Wrapper } from './address.styled';
import { Toggle } from '../../toggle';
import { FiasSelect } from '../fias-select';

interface IProps {
  canCollapsed?: boolean;
  inCallCard?: boolean;
}

export const Address = observer(
  ({ canCollapsed = true, inCallCard }: IProps) => {
    const { createRequest, houses, tenants, meters, telephony, aiRequests } =
      useStore();
    const {
      areas,
      selectedArea,
      anotherAddress,
      requestData,
      setBooleanProperties,
      setRequestData,
    } = createRequest;
    const { getMembersByArea, clearMembers } = createRequest.applicant;

    const [open, setOpen] = useState<boolean>(false);
    const displayedAreas = areas.length
      ? areas
      : selectedArea?.id && selectedArea.id === requestData.area?._id
        ? [selectedArea]
        : [];

    useEffect(() => {
      if (inCallCard && !anotherAddress) {
        if (!!areas[0]?.house.address && !!areas[0]?.str_number_full) {
          telephony.callerInfo.setCallerAddress(areas[0].house.address);
          telephony.callerInfo.setCallerNumber(areas[0].str_number_full);
        } else {
          telephony.callerInfo.setCallerAddress('');
          telephony.callerInfo.setCallerNumber('');
        }
      }
      // Только при монтировании
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isCollapsed: boolean = useMemo(
      () => canCollapsed && !open,
      [canCollapsed, open]
    );

    const openHandler = (): void => {
      setOpen((prevOpen) => !prevOpen);
    };

    const changeRequestIds = (idArea: string, idHouse: string): void => {
      setRequestData(
        Object.assign(cloneDeep(requestData), {
          area: { _id: idArea },
          house: { _id: idHouse },
        })
      );
    };

    const handleChangeHouse = (
      idArea: string,
      idHouse: string,
      strHouse: string,
      strNumber: string
    ): void => {
      if (idArea !== requestData.area?._id) {
        setBooleanProperties('anotherMember', false);
        setBooleanProperties('anotherAddress', false);
        clearMembers();

        changeRequestIds(idArea, idHouse);
        getMembersByArea(idArea, true).then((members: IMember[]) => {
          if (!!members?.length && !!members[0]?.id) {
            tenants.getTenantSectors(members[0].id);
          }

          if (meters.currentAreaId !== idArea) {
            meters.getAreaMetersAllReadings({
              area: idArea,
            });
          }
        });
        houses.getHouseData(idHouse);
      }

      telephony.callerInfo.setCallerAddress(strHouse);
      telephony.callerInfo.setCallerNumber(strNumber);
    };

    const handleChangeAnotherAddress = (): void => {
      setBooleanProperties('anotherMember', false);
      clearMembers();

      changeRequestIds('', '');
      setBooleanProperties('anotherAddress', true);
    };

    return (
      <Wrapper isCollapsed={isCollapsed}>
        {canCollapsed && (
          <ToggleWrapper>
            <Toggle
              open={open}
              onClick={openHandler}
              disabled={!displayedAreas.length || aiRequests.isAiMode}
            />
          </ToggleWrapper>
        )}
        {!displayedAreas.length && <FiasSelect collapsed={isCollapsed} />}
        {displayedAreas.map((item: IArea, index: number) => (
          <Item
            isDisabled={aiRequests.isAiMode}
            canCollapsed={canCollapsed}
            key={index}
            isSelected={!anotherAddress && requestData.area?._id === item.id}
            isCollapsed={isCollapsed}
            onClick={() =>
              handleChangeHouse(
                item.id,
                item.house.id,
                item.house.address,
                item.str_number_full
              )
            }
          >
            <StyledSpan>{`${item.house.address}, ${item.str_number_full}`}</StyledSpan>
          </Item>
        ))}

        {!!displayedAreas.length && anotherAddress ? (
          <FiasSelect collapsed={isCollapsed} />
        ) : (
          <>
            {!aiRequests.isAiMode &&
              (canCollapsed || (!canCollapsed && !!displayedAreas.length)) && (
                <ActionItem
                  isCollapsed={isCollapsed}
                  isSelected={false}
                  onClick={handleChangeAnotherAddress}
                >
                  Другой адрес
                </ActionItem>
              )}
          </>
        )}
      </Wrapper>
    );
  }
);
